import { publicApi as api } from "./publicApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getInventoryByInventoryId: build.query<
      GetInventoryByInventoryIdApiResponse,
      GetInventoryByInventoryIdApiArg
    >({
      query: (queryArg) => ({ url: `/inventory/${queryArg.inventoryId}` })
    }),
    getInventory: build.query<GetInventoryApiResponse, GetInventoryApiArg>({
      query: (queryArg) => ({
        url: `/inventory`,
        params: {
          sku: queryArg.sku,
          expiresOnOrBeforeDate: queryArg.expiresOnOrBeforeDate,
          lotNumber: queryArg.lotNumber,
          binId: queryArg.binId,
          hasHold: queryArg.hasHold,
          offset: queryArg.offset,
          limit: queryArg.limit
        }
      })
    }),
    postInventoryBySkuBySkuPlaceHold: build.mutation<
      PostInventoryBySkuBySkuPlaceHoldApiResponse,
      PostInventoryBySkuBySkuPlaceHoldApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory/by-sku/${queryArg.sku}/place-hold`,
        method: "POST",
        body: queryArg.inventorySkuHold
      })
    }),
    postInventoryBySkuBySkuRemoveHold: build.mutation<
      PostInventoryBySkuBySkuRemoveHoldApiResponse,
      PostInventoryBySkuBySkuRemoveHoldApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory/by-sku/${queryArg.sku}/remove-hold`,
        method: "POST",
        body: queryArg.removeInventoryHoldBySkuRequest
      })
    }),
    getInventoryHolds: build.query<
      GetInventoryHoldsApiResponse,
      GetInventoryHoldsApiArg
    >({
      query: () => ({ url: `/inventory/holds` })
    }),
    getInventoryHoldsByInventoryId: build.query<
      GetInventoryHoldsByInventoryIdApiResponse,
      GetInventoryHoldsByInventoryIdApiArg
    >({
      query: (queryArg) => ({ url: `/inventory/holds/${queryArg.inventoryId}` })
    }),
    postInventoryHoldsByInventoryIdPlace: build.mutation<
      PostInventoryHoldsByInventoryIdPlaceApiResponse,
      PostInventoryHoldsByInventoryIdPlaceApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory/holds/${queryArg.inventoryId}/place`,
        method: "POST",
        body: queryArg.placeInventoryHold
      })
    }),
    postInventoryHoldsByInventoryIdRemove: build.mutation<
      PostInventoryHoldsByInventoryIdRemoveApiResponse,
      PostInventoryHoldsByInventoryIdRemoveApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory/holds/${queryArg.inventoryId}/remove`,
        method: "POST",
        body: queryArg.removeInventoryHold
      })
    }),
    postInventorysnapshotsCreate: build.mutation<
      PostInventorysnapshotsCreateApiResponse,
      PostInventorysnapshotsCreateApiArg
    >({
      query: (queryArg) => ({
        url: `/inventorysnapshots/create`,
        method: "POST",
        body: queryArg.body
      })
    }),
    getInventorysnapshotsByInventorySnapshotId: build.query<
      GetInventorysnapshotsByInventorySnapshotIdApiResponse,
      GetInventorysnapshotsByInventorySnapshotIdApiArg
    >({
      query: (queryArg) => ({
        url: `/inventorysnapshots/${queryArg.inventorySnapshotId}`
      })
    }),
    postInventoryReceive: build.mutation<
      PostInventoryReceiveApiResponse,
      PostInventoryReceiveApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory/receive`,
        method: "POST",
        body: queryArg.receivedInventory
      })
    }),
    getInventoryEvents: build.query<
      GetInventoryEventsApiResponse,
      GetInventoryEventsApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory/events`,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
          fromDateTime: queryArg.fromDateTime
        }
      })
    }),
    getInventoryByAggregateIdEvents: build.query<
      GetInventoryByAggregateIdEventsApiResponse,
      GetInventoryByAggregateIdEventsApiArg
    >({
      query: (queryArg) => ({
        url: `/inventory/${queryArg["aggregate-id"]}/events`,
        params: {
          offset: queryArg.offset,
          fromDateTime: queryArg.fromDateTime,
          limit: queryArg.limit
        }
      })
    }),
    getInventorysnapshotsEvents: build.query<
      GetInventorysnapshotsEventsApiResponse,
      GetInventorysnapshotsEventsApiArg
    >({
      query: (queryArg) => ({
        url: `/inventorysnapshots/events`,
        params: {
          limit: queryArg.limit,
          offset: queryArg.offset,
          fromDateTime: queryArg.fromDateTime
        }
      })
    }),
    getInventorysnapshotsByAggregateIdEvents: build.query<
      GetInventorysnapshotsByAggregateIdEventsApiResponse,
      GetInventorysnapshotsByAggregateIdEventsApiArg
    >({
      query: (queryArg) => ({
        url: `/inventorysnapshots/${queryArg["aggregate-id"]}/events`,
        params: {
          offset: queryArg.offset,
          fromDateTime: queryArg.fromDateTime,
          limit: queryArg.limit
        }
      })
    })
  }),
  overrideExisting: false
});
export { injectedRtkApi as inventoryApi };
export type GetInventoryByInventoryIdApiResponse =
  /** status 200 OK */ InventoryItem;
export type GetInventoryByInventoryIdApiArg = {
  /** AutoStore identifier for the inventory. */
  inventoryId: string;
};
export type GetInventoryApiResponse = /** status 200 OK */ InventoryItemPage;
export type GetInventoryApiArg = {
  /** When specified, returns only inventory matching the given SKU (optional). */
  sku?: string;
  /** When specified, returns only inventory that expires on or before the given date (optional). */
  expiresOnOrBeforeDate?: string;
  /** When specified, returns only inventory matching the given lot number (optional). */
  lotNumber?: string;
  /** When specified, returns only inventory located in the given AutoStore bin ID (optional). */
  binId?: string;
  /** When specified, returns inventory with a hold when true or inventory without a hold when false (optional). */
  hasHold?: boolean;
  /** The number of items to skip before returning results. Default is 0. */
  offset?: number;
  /** The number of items to return. */
  limit?: number;
};
export type PostInventoryBySkuBySkuPlaceHoldApiResponse =
  /** status 202 Accepted */ InventoryHoldBySkuResponse;
export type PostInventoryBySkuBySkuPlaceHoldApiArg = {
  /** The SKU to place a hold on. */
  sku: string;
  inventorySkuHold: InventorySkuHold;
};
export type PostInventoryBySkuBySkuRemoveHoldApiResponse =
  /** status 202 Accepted */ InventoryHoldBySkuResponse;
export type PostInventoryBySkuBySkuRemoveHoldApiArg = {
  /** The SKU to remove a hold from, if it has one */
  sku: string;
  removeInventoryHoldBySkuRequest: RemoveInventoryHoldBySkuRequest;
};
export type GetInventoryHoldsApiResponse = /** status 200 OK */ InventoryHold[];
export type GetInventoryHoldsApiArg = void;
export type GetInventoryHoldsByInventoryIdApiResponse =
  /** status 200 OK */ InventoryHold;
export type GetInventoryHoldsByInventoryIdApiArg = {
  /** AutoStore identifier for the inventory. */
  inventoryId: string;
};
export type PostInventoryHoldsByInventoryIdPlaceApiResponse = unknown;
export type PostInventoryHoldsByInventoryIdPlaceApiArg = {
  /** AutoStore identifier for the inventory. */
  inventoryId: string;
  placeInventoryHold: PlaceInventoryHold;
};
export type PostInventoryHoldsByInventoryIdRemoveApiResponse = unknown;
export type PostInventoryHoldsByInventoryIdRemoveApiArg = {
  /** AutoStore identifier for the inventory. */
  inventoryId: string;
  removeInventoryHold: RemoveInventoryHold;
};
export type PostInventorysnapshotsCreateApiResponse =
  /** status 202 Accepted */ CreateInventorySnapshot;
export type PostInventorysnapshotsCreateApiArg = {
  body: (object | null) | CreateInventorySnapshotRequest;
};
export type GetInventorysnapshotsByInventorySnapshotIdApiResponse =
  /** status 200 OK */ InventorySnapshot;
export type GetInventorysnapshotsByInventorySnapshotIdApiArg = {
  /** The ID of inventory snapshot ID to get. */
  inventorySnapshotId: string;
};
export type PostInventoryReceiveApiResponse =
  /** status 202 Accepted */ ReceivedInventoryResponse;
export type PostInventoryReceiveApiArg = {
  receivedInventory: ReceivedInventory;
};
export type GetInventoryEventsApiResponse = /** status 200 OK */ {
  /** The current feed’s uri */
  feed_url?: string;
  /** The uri for fetching the next page of the feed */
  next_url?: string | null;
  /** A collection of event objects */
  items?: InventoryEvent[];
};
export type GetInventoryEventsApiArg = {
  limit?: number;
  offset?: number;
  fromDateTime?: string;
};
export type GetInventoryByAggregateIdEventsApiResponse = /** status 200 OK */ {
  /** The current feed’s uri */
  feed_url?: string;
  /** The uri for fetching the next page of the feed */
  next_url?: string | null;
  /** A collection of event objects */
  items?: InventoryEvent[];
};
export type GetInventoryByAggregateIdEventsApiArg = {
  /** A unique identifier of an aggregate. For example uuid of PickId, PutAwayTaskId, etc */
  "aggregate-id": string;
  /** Every event will have a sequence number. When providing an offset, only the events containing offset that is equal or greater than provided are returned. */
  offset?: number;
  /** Querying by fromDateTime will yield events since the fromDateTime */
  fromDateTime?: string;
  /** Maximum number of events to include. */
  limit?: number;
};
export type GetInventorysnapshotsEventsApiResponse = /** status 200 OK */ {
  /** The current feed’s uri */
  feed_url?: string;
  /** The uri for fetching the next page of the feed */
  next_url?: string | null;
  /** A collection of event objects */
  items?: InventorySnapshotEvent[];
};
export type GetInventorysnapshotsEventsApiArg = {
  limit?: number;
  offset?: number;
  fromDateTime?: string;
};
export type GetInventorysnapshotsByAggregateIdEventsApiResponse =
  /** status 200 OK */ {
    /** The current feed’s uri */
    feed_url?: string;
    /** The uri for fetching the next page of the feed */
    next_url?: string | null;
    /** A collection of event objects */
    items?: InventorySnapshotEvent[];
  };
export type GetInventorysnapshotsByAggregateIdEventsApiArg = {
  /** A unique identifier of an aggregate. For example uuid of PickId, PutAwayTaskId, etc */
  "aggregate-id": string;
  /** Every event will have a sequence number. When providing an offset, only the events containing offset that is equal or greater than provided are returned. */
  offset?: number;
  /** Querying by fromDateTime will yield events since the fromDateTime */
  fromDateTime?: string;
  /** Maximum number of events to include. */
  limit?: number;
};
export type Quantity = {
  /** The unit */
  unit: string;
  /** The amount of unit */
  value: number;
};
export type PurchaseOrderLineItem = {
  /** The AutoStore ID for the Purchase Order this inventory item is a part of */
  purchaseOrderId?: string;
  /** The Purchase Order Line Item Number for this inventory */
  purchaseOrderLineItemNumber?: string;
};
export type TemperatureZone =
  | ("Ambient" | "Chilled" | "Frozen" | "ambient" | "chilled" | "frozen")
  | null;
export type AutostoreBin = {
  /** The AutoStore grid ID. */
  gridId?: string;
  /** The AutoStore bin number. */
  binNumber?: number;
  /** The AutoStore bin compartment number. */
  compartmentNumber?: number;
  /** The number of compartments in bin */
  numberOfCompartments?: number;
} | null;
export type WarehouseBin = {
  /** The aisle the bin is in. */
  aisle?: string | null;
  /** The shelf bay the bin is in. */
  shelfBay?: string | null;
  /** The pallet bay the bin is in. */
  palletBay?: string | null;
  /** The shelf the bin is on. */
  shelf?: string | null;
  /** The position of the bin. */
  position?: string | null;
} | null;
export type Bin = {
  /** The AutoStore ID for the bin containing the inventory */
  binId?: string;
  /** The bin type the inventory is contained in. If Autostore then the autostoreBin property will be populated, otherwise the warehouseBin will be populated. */
  binType?: string;
  temperatureZone?: TemperatureZone;
  autostoreBin?: AutostoreBin;
  warehouseBin?: WarehouseBin;
};
export type InventoryHoldSource = "internal" | "external" | "system";
export type InventoryHolds = {
  /** The type of hold */
  hold?: string;
  /** The hold details for external holds */
  details?: string | null;
  source?: InventoryHoldSource;
};
export type InventoryItem = {
  /** The AutoStore ID for the inventory item */
  inventoryId?: string;
  /** The AutoStore ID for the variant of the product */
  variantId?: string;
  /** The brand name of the inventory item */
  brand?: string;
  /** The product name of the inventory item */
  product?: string;
  /** The SKU of the inventory item */
  sku?: string;
  /** The quantity of inventory */
  quantity?: Quantity;
  /** The optional expiration date of the inventory item */
  expirationDate?: string | null;
  /** The optional manufacture date of the inventory item */
  manufactureDate?: string | null;
  /** DEPRECATED. PLEASE USE createdAt INSTEAD. The put away date of inventory in this bin. */
  putAway?: string;
  /** The date and time the inventory item was created. */
  createdAt?: string;
  /** An optional lot number for the inventory item */
  lotNumber?: string | null;
  purchaseOrderLineItem?: (object | null) | PurchaseOrderLineItem;
  bin?: Bin;
  holds?: InventoryHolds[];
};
export type ProblemDetails = {
  /** A URI reference that identifies the problem type */
  type?: string | null;
  /** A short, human-readable summary of the problem type */
  title: string;
  /** A human-readable explanation specific to this occurrence of the problem */
  detail?: string | null;
  /** A URI reference that identifies the specific occurrence of the problem */
  instance?: string | null;
};
export type InventoryItemPage = {
  /** The current page's URL */
  current_url?: string;
  /** The next page's URL, null if there are no more items. */
  next_url?: string | null;
  /** A collection of inventory items matching the filters */
  items?: InventoryItem[];
};
export type InventoryHoldBySkuResponse = {
  /** uri for the inventory hold */
  inventoryHoldUri?: string;
};
export type InventoryHoldReasonCode =
  | "bin locked"
  | "bin not empty"
  | "bin problem"
  | "damaged"
  | "expired"
  | "expiration pad time"
  | "external"
  | "food safety"
  | "out of stock"
  | "out of weight threshold"
  | "over received"
  | "recalled"
  | "sanitization status unknown"
  | "wrong compartment";
export type InventorySkuHold = {
  /** Type of hold being placed */
  reasonCode?: InventoryHoldReasonCode;
  /** If specified, the hold is only placed on inventory matching the given lot number. */
  lotNumber?: string | null;
  /** If specified, the hold is only placed on inventory matching the given expiration date. */
  expirationDate?: string | null;
  /** Extra information about the hold. */
  details?: string | null;
};
export type RemoveInventoryHoldBySkuRequest = {
  /** Optional - The type of hold being removed. If not specified, will remove any holds matching this SKU and other fields specified. */
  reasonCode?: InventoryHoldReasonCode | null;
  /** An optional lot number to match when removing the hold. */
  lotNumber?: string | null;
  /** An optional expiration date to match when placing the hold. */
  expirationDate?: string | null;
};
export type InventoryHoldCount = {
  /** Number of the amount of inventory. */
  value?: number;
  /** Unit of the amount of inventory */
  unit?: string;
};
export type InventoryHoldProduct = {
  /** The AutoStore identifier of the Product. */
  id?: string;
  /** Name of the product */
  productName?: string;
  /** The Brand Name of the product */
  brand?: string;
  /** Stock Keeping Unit */
  sku?: string;
  /** Primary UPC or PLU for scanning barcode of the product. */
  upcOrPlu?: string;
  /** Secondary or alternate UPCs that also apply to this product */
  secondaryUpcs?: string[];
  /** Link to an image of the endpoint */
  image?: string;
};
export type InventoryHold = {
  /** AutoStore identifier for the inventory */
  inventoryId?: string;
  /** The type of hold placed on the inventory. “External” is used for holds originating externally. To be deprecated in favor of "holds" */
  hold?: string | null;
  /** If hold type is “External,” this can be populated with any extra details you want. To be deprecated in favor of "holds" */
  holdDetails?: string | null;
  holds?: {
    reasonCode?: InventoryHoldReasonCode;
    /** The externally added data about the hold */
    externalData?: string | null;
    /** The internally added notes about the hold */
    notes?: string | null;
    /** The time when the hold was placed */
    timestamp?: string;
  }[];
  /** DEPRECATED. PLEASE USE expirationDate INSTEAD. Date that the inventory expires (or expired) */
  expiration?: string | null;
  /** Date that the inventory expires (or expired) */
  expirationDate?: string | null;
  /** Manufacture date for the inventory in ISO 8601 format. */
  manufactureDate?: string | null;
  /** Date and time that the inventory was put away. */
  putAway?: string;
  /** Lot number, if it was provided. */
  lotNumber?: string | null;
  count?: InventoryHoldCount;
  product?: InventoryHoldProduct;
  bin?: Bin;
};
export type PlaceInventoryHold = {
  /** Type of hold being placed */
  reasonCode?: InventoryHoldReasonCode;
  /** Extra information about placing inventory on hold */
  details?: string | null;
};
export type RemoveInventoryHold = {
  /** Type of hold being removed */
  reasonCode?: InventoryHoldReasonCode | null;
};
export type CreateInventorySnapshot = {
  /** An optional ID to identify the inventory snapshot generation task */
  inventorySnapshotId?: string;
  /** The URI of the snapshot once it is generated */
  inventorySnapshotUri?: string;
};
export type CreateInventorySnapshotRequest = {
  /** An optional AutoStore grid ID to limit the snapshot to. */
  autostoreGridId?: string | null;
  /** An optional identifier for the Inventory Snapshot. The value will be generated if not provided. */
  inventorySnapshotId?: string | null;
};
export type InventorySnapshot = {
  /** The unique ID of the inventory snapshot */
  inventorySnapshotId?: string;
  /** The offset of the last Inventory Event that took place prior to the snapshot */
  lastInventoryAdjustmentEventOffset?: number;
  /** A collection of inventory items that make up the snapshot */
  inventory?: InventoryItem[];
};
export type ReceivedInventoryResponseFailureItem = {
  /** The error code. */
  errorCode?: string;
  /** The message detailing what's wrong. */
  errorMessage?: string;
};
export type ReceivedInventoryResponsePurchaseOrderLineItem = {
  /** Line Item number */
  purchaseOrderLineItemNumber?: string | null;
  /** Purchase Order number */
  purchaseOrderNumber?: string | null;
  /** AutoStore identifier for the Purchase Order. */
  purchaseOrderId?: string;
};
export type ReceivedInventoryResponseFailure = {
  /** The list of errors for the given inventory line item. */
  errors?: ReceivedInventoryResponseFailureItem[];
  purchaseOrderLineItem?:
    | (object | null)
    | ReceivedInventoryResponsePurchaseOrderLineItem;
  /** Stock Keeping Unit */
  sku?: string | null;
  /** Primary UPC or PLU for scanning barcode of the product */
  upcOrPlu?: string | null;
};
export type ReceivedInventoryResponseSuccess = {
  /** AutoStore identifier for the inventory created for the given line item of inventory. */
  inventoryId?: string;
  purchaseOrderLineItem?:
    | (object | null)
    | ReceivedInventoryResponsePurchaseOrderLineItem;
};
export type ReceivedInventoryResponse = {
  /** The top level status indicating success or failure of the group of inventory. */
  status?: string;
  /** All records that failed to process. Note: If FailAllIfAny was false, Successes and Failures may both be populated. */
  failures?: ReceivedInventoryResponseFailure[];
  /** All records that successfully processed and created inventory and putaway tasks. Note: If FailAllIfAny was false, Successes and Failures may both be populated. */
  successes?: ReceivedInventoryResponseSuccess[];
};
export type ReceivedInventoryItemCount = {
  unit: string;
  /** Amount of items to be added / subtracted (negative number) */
  value: number;
};
export type ReceivedInventoryItemDecantingRate = {
  unit?: string;
  /** The amount of the DecantingRate’s Units that are included in one count. */
  value?: number;
};
export type ReceivedInventoryItem = {
  /** Used if tracking a Batch Code, Pallet Code or Lot Code, etc. */
  code?: string | null;
  count: ReceivedInventoryItemCount;
  decantingRate?: (object | null) | ReceivedInventoryItemDecantingRate;
  /** Expiration date for the inventory in ISO 8601 format. */
  expiration?: string | null;
  /** Manufacture date for the inventory in ISO 8601 format. */
  manufactureDate?: string | null;
  /** Lot number */
  lotNumber?: string | null;
  /** Line Item number within the Purchase Order. Does not have to be unique. */
  purchaseOrderLineItemNumber?: string | null;
  /** Purchase Order number. Does not have to be unique. */
  purchaseOrderNumber?: string | null;
  /** Stock Keeping Unit */
  sku: string;
  /** Primary UPC or PLU for scanning barcode of the product */
  upcOrPlu?: string | null;
};
export type ReceivedInventory = {
  /** All of the inventory to receive and put away. */
  receivings: ReceivedInventoryItem[];
  /** Whether to fail the whole group of inventory if any of the group of inventory fails to validate or process. */
  failAllIfAnyFail?: boolean | null;
  /** An idempotency key for the request. Prevents duplicate calls from being made for a given Receive. */
  externalReceivingId?: string | null;
};
export type EventFeedItemMetadata = {
  /** A unique identifier of a user or an automated process that triggered the event */
  userId?: string | null;
};
export type Event = {
  /** A unique identifier of an aggregate the event belongs to. For example PickId, PutAwayTaskId, etc */
  _aggregateId: string;
  /** Sequentially incremented number representing casual order of events. It is used to fetch next portion/page of events to process from the feed */
  _offset: number;
  date_published: string;
  /** The event type in the format: aggregateName.eventName */
  type: string;
  id: string;
  url?: string;
  _metadata?: EventFeedItemMetadata;
};
export type InventoryEventBase = Event & object;
export type BinType =
  | (
      | "Autostore"
      | "Shelf"
      | "Pallet"
      | "Back Stock"
      | "Bulk"
      | "Receiving"
      | "Induction"
    )
  | null;
export type InventoryHoldReleased = {
  type: "Inventory.HoldReleased";
} & InventoryEventBase & {
    _data?: {
      /** Current inventory amount before the adjustment */
      currentInventory?: Quantity;
      /** The retailer-specific ID for the product */
      sku?: string;
      /** The primary UPC/PLU for scanning the barcode of the product */
      upcOrPlu?: string;
      /** The AutoStore bin ID */
      binId?: string;
      /** The lot number associated with the inventory item */
      lotNumber?: string | null;
      /** The bin the inventory is contained in */
      bin?: {
        /** The Bin ID. */
        binId?: string;
        binType?: BinType;
        temperatureZone?: TemperatureZone;
        autostoreBin?: AutostoreBin;
        warehouseBin?: WarehouseBin;
      };
      /** The ID of the workstation where the hold was placed */
      workstationId?: string | null;
      reasonCode?: InventoryHoldReasonCode;
      /** The AutoStore ID for the variant of the product */
      variantId?: string;
      /** The device name of the workstation the hold was placed */
      deviceId?: string | null;
      /** Purchase Order Line Item info, including ID and Line Item Number */
      purchaseOrderLineItem?: {
        /** AutoStore ID of the Purchase Order */
        purchaseOrderId?: string | null;
        /** Purchase Order Line Item Number */
        purchaseOrderLineItemNumber?: string | null;
      } | null;
    };
  };
export type InventoryHoldPlaced = {
  type: "Inventory.HoldPlaced";
} & InventoryEventBase & {
    _data?: {
      /** Current inventory amount before the adjustment. */
      currentInventory?: Quantity;
      /** The retailer-specific ID for the product */
      sku?: string;
      /** The primary UPC/PLU for scanning the barcode of the product */
      upcOrPlu?: string;
      /** The AutoStore bin ID */
      binId?: string;
      /** The lot number associated with the inventory item */
      lotNumber?: string | null;
      /** The bin the inventory is contained in */
      bin?: {
        /** The Bin ID. */
        binId?: string;
        binType?: BinType;
        temperatureZone?: TemperatureZone;
        autostoreBin?: AutostoreBin;
        warehouseBin?: WarehouseBin;
      };
      /** Extra information about the hold */
      details?: string | null;
      /** The ID of the workstation the hold was placed */
      workstationId?: string | null;
      reasonCode?: InventoryHoldReasonCode;
      /** The AutoStore ID for the variant of the product */
      variantId?: string;
      /** The device name of the workstation the hold was place */
      deviceId?: string | null;
      /** Purchase Order Line Item info, including ID and Line Item Number */
      purchaseOrderLineItem?: {
        /** AutoStore ID of the Purchase Order */
        purchaseOrderId?: string | null;
        /** Purchase Order Line Item Number */
        purchaseOrderLineItemNumber?: string | null;
      } | null;
    };
  };
export type InventoryPlaced = {
  type: "Inventory.Placed";
} & InventoryEventBase & {
    _data?: {
      /** If the Inventory was moved from somewhere else, will contain the AutoStore inventory item ID for that record */
      previousInventoryId?: string | null;
      /** The retailer-specific ID for the product */
      sku?: string;
      /** Any holds that are on the inventory that was placed */
      holds?:
        | {
            /** Type of hold being placed */
            hold?: InventoryHoldReasonCode;
            source?: InventoryHoldSource;
            details?: string | null;
          }[]
        | null;
      /** The primary UPC/PLU for scanning the barcode of the product */
      upcOrPlu?: string;
      /** The date the inventory was manufactured */
      manufactureDate?: string | null;
      /** Inventory quantity that was received */
      quantity?: Quantity;
      /** The AutoStore product name */
      productName?: string | null;
      /** The lot number associated with the inventory item */
      lotNumber?: string | null;
      /** The bin the inventory is in */
      bin?: {
        /** The Bin ID. */
        binId?: string;
        binType?: BinType;
        temperatureZone?: TemperatureZone;
        autostoreBin?: AutostoreBin;
        warehouseBin?: WarehouseBin;
      };
      /** Workstation on which the adjustment was made */
      workstationId?: string | null;
      /** DEPRECATED. PLEASE USE expirationDate INSTEAD. The expiration date of the inventory */
      expiration?: string | null;
      /** The expiration date of the inventory */
      expirationDate?: string | null;
      /** The AutoStore variant ID */
      variantId?: string;
      /** The device name of the workstation which the adjustment was made */
      deviceId?: string | null;
      /** Purchase Order Line Item info, including ID and Line Item Number */
      purchaseOrderLineItem?: {
        /** AutoStore ID of the Purchase Order */
        purchaseOrderId?: string | null;
        /** Purchase Order Line Item Number */
        purchaseOrderLineItemNumber?: string | null;
      } | null;
    };
  };
export type InventoryInventoryMoved = {
  type: "Inventory.InventoryMoved";
} & InventoryEventBase & {
    _data?: {
      /** Inventory Id of the destination inventory */
      destinationInventoryId?: string;
      /** Amount of inventory in the destination bin after the move */
      destinationNewQuantity?: Quantity;
      /** The retailer-specific ID for the product */
      sku?: string;
      /** The primary UPC/PLU for scanning the barcode of the product */
      upcOrPlu?: string;
      /** The lot number associated with the inventory item */
      lotNumber?: string | null;
      /** Amount of inventory in the source bin before the move */
      sourceOriginalQuantity?: Quantity;
      /** Bin that the inventory was moved to */
      destinationBinId?: string;
      /** Inventory amount that was moved */
      quantityMoved?: Quantity;
      /** Workstation from which the move was initiated */
      workstationId?: string | null;
      /** Bin that the inventory was moved from */
      sourceBinId?: string;
      /** The AutoStore ID for the variant of the product */
      variantId?: string;
    };
  };
export type InventoryReceived = {
  type: "Inventory.Received";
} & InventoryEventBase & {
    _data?: {
      /** The retailer-specific ID for the product */
      sku?: string;
      /** The primary UPC/PLU for scanning the barcode of the product */
      upcOrPlu?: string;
      /** The date the inventory was manufactured */
      manufactureDate?: string | null;
      /** Inventory quantity that was received */
      quantity?: Quantity;
      /** The AutoStore product name */
      productName?: string | null;
      /** The lot number associated with the inventory item */
      lotNumber?: string | null;
      /** The bin the inventory is in */
      bin?: {
        /** The Bin ID. */
        binId?: string;
        binType?: BinType;
        temperatureZone?: TemperatureZone;
        autostoreBin?: AutostoreBin;
        warehouseBin?: WarehouseBin;
      };
      /** DEPRECATED. PLEASE USE expirationDate INSTEAD. The expiration date of the inventory */
      expiration?: string | null;
      /** The expiration date of the inventory */
      expirationDate?: string | null;
      /** The AutoStore variant ID */
      variantId?: string;
      /** Purchase Order Line Item info, including ID and Line Item Number */
      purchaseOrderLineItem?: {
        /** AutoStore ID of the Purchase Order */
        purchaseOrderId?: string | null;
        /** Purchase Order Line Item Number */
        purchaseOrderLineItemNumber?: string | null;
      } | null;
    };
  };
export type InventoryAdjustmentReasonCode =
  | "Added"
  | "Cancelled"
  | "Order Batched"
  | "Order Cancelled"
  | "Counted"
  | "Cycle Counted"
  | "Moved"
  | "Picked"
  | "PutAway"
  | "Received"
  | "Replenished"
  | "Reverted"
  | "Hold Placed"
  | "Hold Removed"
  | "Out Of Stocked"
  | "Rescheduled"
  | "Expiration Corrected"
  | "Putaway Corrected"
  | "Damaged"
  | "Quality"
  | "Missing"
  | "Expired"
  | "Transfer"
  | "Found"
  | "Customer Return"
  | "Received Expired"
  | "Received Missing"
  | "Over Received"
  | "Expired Donate"
  | "Expired Destroy"
  | "Vendor Expired Donate"
  | "Vendor Expired Destroy"
  | "Damaged Donate"
  | "Damaged Destroy"
  | "Vendor Damaged Donate"
  | "Vendor Damaged Destroy"
  | "Warehouse Damaged Donate"
  | "Warehouse Damaged Destroy"
  | "Culling/Quality Donate"
  | "Culling/Quality Destroy"
  | "In-Store Out Of Temp Destroy"
  | "Recalled Destroy"
  | "Sanitization Status Unknown"
  | "Restock"
  | "Bin Not Empty";
export type InventoryInventoryAdjusted = {
  type: "Inventory.InventoryAdjusted";
} & InventoryEventBase & {
    _data?: {
      /** Current inventory amount before the adjustment */
      currentInventory?: Quantity;
      /** The AutoStore inventory item ID */
      inventoryId?: string;
      /** The retailer-specific ID for the product */
      sku?: string;
      /** The primary UPC/PLU for scanning the barcode of the product */
      upcOrPlu?: string;
      /** The AutoStore product ID */
      productId?: string;
      /** Inventory amount after the adjustment */
      quantityAfter?: Quantity;
      /** The AutoStore bin ID */
      binId?: string;
      /** The date the inventory was manufactured */
      manufactureDate?: string | null;
      /** The user making the adjustment */
      userId?: string | null;
      /** The AutoStore product name */
      productName?: string | null;
      /** The lot number associated with the inventory item */
      lotNumber?: string | null;
      /** The bin the inventory is in */
      bin?: {
        /** The Bin ID. */
        binId?: string;
        binType?: BinType;
        temperatureZone?: TemperatureZone;
        autostoreBin?: AutostoreBin;
        warehouseBin?: WarehouseBin;
      };
      /** Workstation on which the adjustment was made */
      workstationId?: string | null;
      /** DEPRECATED. PLEASE USE expirationDate INSTEAD. The expiration date of the inventory */
      expiration?: string | null;
      /** The expiration date of the inventory after adjustment, whether or not it changed. */
      expirationDate?: string | null;
      /** The previous expiration date of the inventory before adjustment, whether or not it changed. */
      previousExpirationDate?: string | null;
      reasonCode?: InventoryAdjustmentReasonCode;
      /** Any additional details about the inventory adjustment. */
      details?: string | null;
      /** The adjustment made to the inventory */
      inventoryAdjustment?: Quantity;
      /** The device name of the workstation which the adjustment was made */
      deviceId?: string | null;
      /** Purchase Order Line Item info, including ID and Line Item Number */
      purchaseOrderLineItem?: {
        /** AutoStore ID of the Purchase Order */
        purchaseOrderId?: string | null;
        /** Purchase Order Line Item Number */
        purchaseOrderLineItemNumber?: string | null;
      } | null;
    };
  };
export type InventoryInventoryStowed = {
  type: "Inventory.InventoryStowed";
} & InventoryEventBase & {
    _data?: {
      /** The AutoStore ID for the variant of the product */
      variantId?: string;
      /** The retailer-specific ID for the product */
      sku?: string;
      /** The bin the inventory is contained in */
      bin?: {
        /** The Bin ID. */
        binId?: string;
        binType?: BinType;
        temperatureZone?: TemperatureZone;
        autostoreBin?: AutostoreBin;
        warehouseBin?: WarehouseBin;
      };
      /** The amount of time the bin spent outside the product's temperature zone */
      elapsedTimeOutsideTempZone?: string;
    };
  };
export type InventoryEvent =
  | InventoryHoldReleased
  | InventoryHoldPlaced
  | InventoryPlaced
  | InventoryInventoryMoved
  | InventoryReceived
  | InventoryInventoryAdjusted
  | InventoryInventoryStowed;
export type InventorySnapshotEventBase = Event & object;
export type InventorySnapshotSnapshotFailed = {
  type: "InventorySnapshot.SnapshotFailed";
} & InventorySnapshotEventBase & {
    _data?: {
      /** The URI of the snapshot being generated */
      snapshotUri?: string | null;
      /** An array of reasons why the snapshot failed */
      reason?: string[] | null;
    };
  };
export type InventorySnapshotSnapshotRequested = {
  type: "InventorySnapshot.SnapshotRequested";
} & InventorySnapshotEventBase & {
    _data?: {
      /** The URI of the snapshot being generated */
      snapshotUri?: string | null;
      /** An optional ID which indicates the specific grid the snapshot was generated for */
      autostoreGridId?: string | null;
    };
  };
export type InventorySnapshotSnapshotSucceeded = {
  type: "InventorySnapshot.SnapshotSucceeded";
} & InventorySnapshotEventBase & {
    _data?: {
      /** The URI of the snapshot being generated */
      snapshotUri?: string | null;
      /** An optional ID which indicates the specific grid the snapshot was generated for */
      autostoreGridId?: string | null;
    };
  };
export type InventorySnapshotEvent =
  | InventorySnapshotSnapshotFailed
  | InventorySnapshotSnapshotRequested
  | InventorySnapshotSnapshotSucceeded;
export const {
  useGetInventoryByInventoryIdQuery,
  useGetInventoryQuery,
  usePostInventoryBySkuBySkuPlaceHoldMutation,
  usePostInventoryBySkuBySkuRemoveHoldMutation,
  useGetInventoryHoldsQuery,
  useGetInventoryHoldsByInventoryIdQuery,
  usePostInventoryHoldsByInventoryIdPlaceMutation,
  usePostInventoryHoldsByInventoryIdRemoveMutation,
  usePostInventorysnapshotsCreateMutation,
  useGetInventorysnapshotsByInventorySnapshotIdQuery,
  usePostInventoryReceiveMutation,
  useGetInventoryEventsQuery,
  useGetInventoryByAggregateIdEventsQuery,
  useGetInventorysnapshotsEventsQuery,
  useGetInventorysnapshotsByAggregateIdEventsQuery
} = injectedRtkApi;
